<template>
  <v-modal
    :short="
      [
        STAGES.PASSWORDLESS,
        STAGES.EMAIL,
        STAGES.DOWNLOAD,
        STAGES.NOT_RECOGNISED
      ].includes(stage)
    "
    class="auth-modal"
    :disable-close="shouldDisableClose"
    @open="handleOpen"
    @close="handleModalClose"
    @initialOpen="handleInitialOpen"
  >
    <template v-if="showOverlay" v-slot:overlay>
      <blocks-container v-if="overlayBlocks" :blocks="overlayBlocks" />
    </template>

    <template v-if="showAvatar" v-slot:before>
      <avatar :image="appIcon" :altText="appName" />
    </template>

    <template :slot="STAGES.PASSWORDLESS">
      <v-loading />
    </template>

    <template :slot="STAGES.TOKEN_SENT">
      <div class="auth-modal__content">
        <email-sent />
      </div>
    </template>

    <template :slot="STAGES.DOWNLOAD">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.welcome", { appName: appName }) }}.
        </h2>
        <p class="lead mb-2 mt-0">
          {{ $t("components.authModal.installApp") }}
        </p>

        <add-to-homescreen context="auth_flow" />
        <v-button
          :is-loading="isLoading"
          block
          class="mt-2"
          ghost
          @click="closeModal"
        >
          {{ $t("components.authModal.installAppDecline") }}
        </v-button>
      </div>
    </template>

    <template :slot="STAGES.EMAIL">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-2">{{ $t("components.authModal.emailHeading") }}</h2>
        <form @submit.prevent="fetchUser">
          <div class="mb-2">
            <v-input
              required
              float
              :placeholder="$t('components.authModal.emailInputPlaceholder')"
              v-model="email"
              type="email"
              name="email"
            ></v-input>
          </div>
          <v-button :is-loading="isLoading" block class="mb-1" type="submit">
            {{ $t("components.authModal.emailCta") }}
          </v-button>
        </form>
      </div>
    </template>

    <template :slot="STAGES.LOGIN">
      <div class="auth-modal__content">
        <login-form :email="email" @login="handleLogin" />
        <v-button
          @click="() => openModal(STAGES.REQUEST_RESET_PASSWORD)"
          block
          ghost
        >
          {{ $t("components.authModal.forgotPasswordCta") }}
        </v-button>
      </div>
    </template>

    <template :slot="STAGES.RESET_PASSWORD">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">{{ $t("components.authModal.setPasswordHeading") }}</h2>
        <p class="lead mb-2 mt-0">
          {{ $t("components.authModal.resetPasswordDescription") }}.
        </p>
        <set-password-form @submit="attemptResetPassword" />
        <v-button @click="() => openModal(STAGES.LOGIN)" block ghost>
          {{ $t("components.authModal.resetPasswordBackCta") }}
        </v-button>
      </div>
    </template>

    <template :slot="STAGES.RESET_PASSWORD_VICPOL">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.resetPasswordVicpolHeading") }}
        </h2>
        <p class="lead mb-2 mt-0">
          {{ $t("components.authModal.resetPasswordVicpolSubheading") }}
        </p>
        <form @submit.prevent="attemptResetVicpolPassword">
          <div class="mb-2">
            <v-input
              required
              :placeholder="
                $t('components.authModal.resetPasswordVicpolPasswordPlaceholder')
              "
              v-model="password"
              type="password"
              name="password"
              autofocus
            ></v-input>
          </div>
          <div class="mb-2">
            <v-input
              required
              :placeholder="
                $t(
                  'components.authModal.resetPasswordVicpolPasswordConfirmationPlaceholder'
                )
              "
              v-model="passwordConfirmation"
              type="password"
              name="passwordConfirmation"
              autofocus
            ></v-input>
          </div>
          <v-button :is-loading="isLoading" block class="mb-2" type="submit">
            {{ $t("components.authModal.resetPasswordVicpolResetCta") }}
          </v-button>
        </form>
        <v-button @click="() => openModal(STAGES.EMAIL)" block ghost>
          {{ $t("components.authModal.resetPasswordVicpolEmailCta") }}
        </v-button>
      </div>
    </template>

    <template :slot="STAGES.REQUEST_RESET_PASSWORD">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.requestResetPasswordHeading") }}
        </h2>
        <p class="lead mb-2 mt-0">
          {{ $t("components.authModal.requestResetPasswordSubheading") }}
        </p>
        <form @submit.prevent="attemptRequestResetPassword">
          <div class="mb-2">
            <v-input
              :placeholder="
                $t('components.authModal.requestResetPasswordEmailPlaceholder')
              "
              type="email"
              name="email"
              v-model="email"
            ></v-input>
          </div>
          <div class="d-flex align-items-center">
            <v-button
              round
              secondary
              class="mr-2"
              type="button"
              @click.prevent="() => openModal(STAGES.LOGIN)"
            >
              <v-icon icon="arrow_back"></v-icon>
            </v-button>
            <v-button :is-loading="isLoading" block type="submit">
              {{ $t("components.authModal.requestResetPasswordCta") }}
            </v-button>
          </div>
        </form>
      </div>
    </template>

    <template :slot="STAGES.SET_PASSWORD">
      <div class="auth-modal__content">
        <template v-if="isExistingAccount">
          <h2 class="mt-0 mb-1">{{ $t("components.authModal.setPasswordHeading") }}</h2>
          <p class="lead mb-3 mt-0">
            {{ $t("components.authModal.setPasswordSubheading") }}
          </p>
          <set-password-form @submit="handleCreateAccount" />
        </template>
        <template v-else>
          <h2 class="mt-0 mb-1">
            {{ $t("components.authModal.createAccountHeading") }}
          </h2>
          <p class="lead mb-3 mt-0">
            {{ $t("components.authModal.createAccountSubheading") }}
          </p>
          <create-account-form
            :email="email"
            @back="() => openModal(STAGES.EMAIL)"
            @submit="handleCreateAccount"
          />
        </template>
      </div>
    </template>

    <template :slot="STAGES.PASSWORDLESS_ERROR">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">
          {{ $t("components.authModal.passwordlessErrorHeading") }}
        </h2>
        <p class="lead mb-2">
          {{ $t("components.authModal.passwordlessErrorSubheading") }}
        </p>
        <form @submit.prevent="attemptRequestPasswordless">
          <div class="mb-2">
            <v-input
              :placeholder="$t('components.authModal.passwordlessErrorEmailPlaceholder')"
              v-model="email"
              type="email"
              name="email"
            ></v-input>
          </div>
          <v-button :is-loading="isLoading" block class="mb-1" type="submit">
            {{ $t("components.authModal.passwordlessErrorCta") }}
          </v-button>
        </form>
      </div>
    </template>

    <template :slot="STAGES.NOT_RECOGNISED">
      <div class="auth-modal__content">
        <h2 class="mt-0 mb-1">{{ $t("components.authModal.notRecognisedHeading") }}</h2>
        <p class="lead mt-0">
          {{ $t("components.authModal.notRecognizedSubheading") }}
        </p>

        <v-button block class="mt-2 mb-1" @click="openModal(STAGES.EMAIL)">
          {{ $t("components.authModal.notRecognizedBackCta") }}
        </v-button>
        <v-button block ghost :to="`mailto:${ownerEmail}`" target="_top">
          {{ $t("components.authModal.notRecognizedContactOwnerCta") }}
        </v-button>
      </div>
    </template>
  </v-modal>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"

import Avatar from "@/components/Avatar"
import vInput from "@/components/forms/vInput"

import EventBus, { EVENTS } from "@/lib/event-bus"
import { isPresent, findAsync } from "@/lib/utils"
import { triggerBlockAction } from "@/lib/stream-utils"

const STAGES = {
  EMAIL: "#auth",
  LOGIN: "#auth/login",
  DOWNLOAD: "#auth/download",
  RESET_PASSWORD: "#auth/reset_password",
  RESET_PASSWORD_VICPOL: "#auth/reset_password/vicpol",
  REQUEST_RESET_PASSWORD: "#auth/reset_password/request",
  SET_PASSWORD: "#auth/set_password",
  TOKEN_SENT: "#auth/token_sent",
  PASSWORDLESS: "#auth/passwordless",
  PASSWORDLESS_ERROR: "#auth/passwordless/error",
  NOT_RECOGNISED: "#auth/not_recognised"
}

export default {
  data() {
    return {
      stage: null,
      name: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      isLoading: false,
      isExistingAccount: false,
      isExpressAuth: false,
      isClosing: false,
      STAGES
    }
  },

  components: {
    vInput,
    Avatar,
    EmailSent: () => import("@/components/auth/EmailSent"),
    LoginForm: () => import("@/components/auth/LoginForm"),
    CreateAccountForm: () => import("@/components/auth/CreateAccountForm"),
    SetPasswordForm: () => import("@/components/auth/SetPasswordForm"),
    AddToHomescreen: () => import("@/components/AddToHomescreen"),
    BlocksContainer: () => import("@/components/stream/BlocksContainer")
  },

  computed: {
    ...mapState(["global"]),
    ...mapGetters("forms", ["authSignedUpForm"]),
    ...mapGetters("pwa", ["canPrompt"]),
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters("access", [
      "requiresAuthentication",
      "appPurchaseOverlayId",
      "isSubscriberOnly",
      "isPrivateApp"
    ]),
    ...mapGetters("products", ["getAppProducts", "hasPastDueSubscription"]),
    ...mapGetters(["appName", "appIcon", "appAccess"]),

    overlayBlocks() {
      const overlay = this.global?.access.auth_overlay

      return overlay && overlay.stream ? overlay.stream.blocks : null
    },

    showAvatar() {
      return !this.showOverlay || this.isExpressAuth
    },

    showOverlay() {
      return (
        isPresent(this.overlayBlocks) &&
        !this.isExpressAuth &&
        [STAGES.EMAIL, STAGES.SET_PASSWORD].includes(this.stage)
      )
    },

    shouldPromptPurchasePostSignup() {
      return (
        !!this.appAccess.prompt_purchase_post_sign_up &&
        this.isSubscriberOnly &&
        this.getAppProducts.length > 0
      )
    },

    shouldPostSignUpAction() {
      return !!this.appAccess.post_sign_up_action
    },

    shouldPostPurchaseScreen() {
      return (
        this.appAccess.is_subscriber_only &&
        !this.appAccess.allow_anonymous_browsing &&
        !this.$route.path.startsWith("/me/")
      )
    },

    postSignUpAction() {
      return this.appAccess.post_sign_up_action
    },

    shouldDisableClose() {
      return (
        !this.appAccess.allow_anonymous_browsing &&
        (this.appAccess.require_sign_in || this.appAccess.is_subscriber_only)
      )
    },

    ownerEmail() {
      return this.global?.account?.email
    }
  },

  methods: {
    ...mapActions("auth", [
      "signUp",
      "fetchUserAuth",
      "setPassword",
      "resetPassword",
      "resetPasswordVicpol",
      "requestResetPassword",
      "verifyPasswordlessAuth",
      "requestPasswordlessAuth"
    ]),
    ...mapActions("analytics", ["trackActivity"]),
    ...mapActions("products", ["hasActivePurchase"]),

    attemptRequestPasswordless() {
      this.isLoading = true

      this.requestPasswordlessAuth({ email: this.email })
        .then(() => this.openModal(STAGES.TOKEN_SENT))
        .catch(err => {
          if (err.reason === "password_required") {
            this.openModal(STAGES.LOGIN)
          } else {
            this.handleError(err)
          }
        })
    },

    handleCreateAccount({ password, name }) {
      this.password = password
      this.name = name

      if (this.isExistingAccount) {
        this.attemptSetPassword()
      } else {
        this.attemptSignUp()
      }
    },

    attemptSignUp() {
      if (this.email) {
        this.isLoading = true

        this.signUp({
          name: this.name,
          email: this.email,
          password: this.password
        })
          .then(() => this.handleSuccessfullAuth(true))
          .catch(this.handleError)
      } else {
        this.openModal(STAGES.EMAIL)
      }
    },

    attemptSetPassword() {
      this.isLoading = true

      this.setPassword({ password: this.password })
        .then(() => this.handleSuccessfullAuth())
        .catch(this.handleError)
    },

    attemptRequestResetPassword() {
      this.isLoading = true

      this.requestResetPassword({ email: this.email })
        .then(() => this.openModal(STAGES.TOKEN_SENT))
        .catch(this.handleError)
        .then(() => (this.isLoading = false))
    },

    attemptResetVicpolPassword() {
      this.isLoading = true

      this.resetPasswordVicpol({
        email: this.email,
        password: this.password,
        passwordConfirmation: this.passwordConfirmation
      })
        .then(() => {
          this.$notify(this.$t("components.authModal.resetPasswordVicpolSuccess"))
          this.handleSuccessfullAuth(true)
        })
        .catch(this.handleError)
        .then(() => (this.isLoading = false))
    },

    attemptResetPassword({ password, passwordConfirmation }) {
      this.isLoading = true

      const { token } = this.$route.query

      this.resetPassword({
        token,
        password,
        passwordConfirmation
      })
        .then(() => {
          this.$notify(this.$t("components.authModal.resetPasswordSuccess"))
          this.closeModal()
        })
        .catch(this.handleError)
        .then(() => (this.isLoading = false))
    },

    attemptVerifyPasswordless() {
      const { auth_token } = this.$route.query

      this.verifyPasswordlessAuth({ token: auth_token })
        .then(() => {
          this.isExistingAccount = true
          setTimeout(() => this.openModal(STAGES.SET_PASSWORD), 100)
        })
        .catch(err => {
          if (err.reason && err.reason === "password_required") {
            this.email = err.user_email
            setTimeout(() => this.openModal(STAGES.LOGIN), 100)
          } else {
            this.$notify(err.message)
            setTimeout(() => this.openModal(STAGES.PASSWORDLESS_ERROR), 100)
          }
        })
        .finally(() => {
          this.$router.replace({
            ...this.$router.currentRoute,
            query: { auth_token: undefined }
          })
        })
    },

    handleSuccessfullAuth(signUp = false) {
      if (this.isExpressAuth) {
        this.closeModal()

        EventBus.$emit(EVENTS.POST_AUTH_FLOW)
      } else if (signUp && this.shouldPostSignUpAction) {
        this.closeModal()

        EventBus.$once(EVENTS.POSTING_SIGN_UP_ACTION)
        setTimeout(() => {
          triggerBlockAction(this.postSignUpAction, false, true)
        }, 150)
      } else if (!signUp && this.hasPastDueSubscription) {
        this.closeModal()

        setTimeout(() => {
          EventBus.$emit(EVENTS.TRIGGER_PAST_DUE_PROMPT, {})
        }, 250)
      } else if (this.shouldPromptPurchasePostSignup) {
        this.closeModal()

        findAsync(
          this.getAppProducts,
          async product => await this.hasActivePurchase(product.id)
        ).then(hasPurchased => {
          if (!hasPurchased) {
            EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
              productIds: this.getAppProducts.map(p => p.id),
              overlay: this.appPurchaseOverlayId,
              global: true
            })
          }
        })
      } else {
        this.openModal(STAGES.DOWNLOAD)
      }
    },

    handleError(err) {
      this.$notify(err.message)
      this.isLoading = false
    },

    fetchUser() {
      this.isLoading = true
      const args = { email: this.email }
      this.fetchUserAuth(args).then(res => {
        this.isExistingAccount = res.exists

        if (!res.exists) {
          if (this.isPrivateApp) {
            this.openModal(STAGES.NOT_RECOGNISED)
          } else {
            this.openModal(STAGES.SET_PASSWORD)
          }
        } else if (res.exists && res.is_legacy_vicpol) {
          this.openModal(STAGES.RESET_PASSWORD_VICPOL)
        } else if (res.exists && res.passwordless) {
          this.attemptRequestPasswordless()
        } else {
          this.openModal(STAGES.LOGIN)
        }
      })
    },

    openModal(hash) {
      this.$router.push(hash)
    },

    handleInitialOpen() {
      this.track("auth_started")
    },

    trackAborted() {
      if (!this.isAuthenticated) {
        EventBus.$emit(EVENTS.AUTH_ABORTED)
        this.track("auth_aborted")
      }
    },

    handleModalClose() {
      if (!this.isAuthenticated) this.trackAborted()
    },

    handleLogin() {
      this.$notify(this.$t("components.authModal.loginSuccess"))
      this.handleSuccessfullAuth()
    },

    closeModal() {
      if (this.isClosing) return

      this.isClosing = true

      if (!this.isAuthenticated) this.trackAborted()
      this.$router.removeHash().then(() => (this.isClosing = false))
    },

    handleOpen(hash) {
      if (
        this.isAuthenticated &&
        ![
          STAGES.DOWNLOAD,
          STAGES.SET_PASSWORD,
          STAGES.PASSWORDLESS,
          STAGES.PASSWORDLESS_ERROR
        ].includes(hash)
      ) {
        this.closeModal()
      } else {
        this.isExpressAuth = EventBus.hasListener(EVENTS.POST_AUTH_FLOW)
      }
    },

    track(activityType, data = {}) {
      this.trackActivity({ activityType, data })
    }
  },

  watch: {
    "$route.hash": {
      immediate: true,
      handler(hash) {
        if (Object.values(STAGES).includes(hash)) this.stage = hash
      }
    },

    isAuthenticated: {
      immediate: true,
      handler() {
        if (this.isAuthenticated && this.stage === STAGES.EMAIL) {
          this.closeModal()
        }
      }
    },

    stage: {
      immediate: true,
      handler(stage) {
        this.isLoading = false

        if (stage && ![STAGES.EMAIL, STAGES.DOWNLOAD].includes(stage)) {
          this.track("auth_progressed", { stage })
        }

        if (stage === STAGES.DOWNLOAD) {
          if (!this.canPrompt) {
            this.closeModal()
          } else {
            this.track("auth_progressed", { stage })
          }
        }

        if (stage === STAGES.PASSWORDLESS && this.$route.query.auth_token) {
          this.attemptVerifyPasswordless()
        }

        if (
          stage === STAGES.NOT_RECOGNISED &&
          !this.isPrivateApp &&
          !this.email
        ) {
          this.openModal(STAGES.EMAIL)
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
.auth-modal__content {
  padding: 0 $size--4 $size--4;
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
